<script setup lang="ts">
import { useGeneralStore } from '~/store'
import { getClientAgeGateSnapshot } from '~/utils/age-gate'

defineOptions({ name: 'ModalAgeVerification' })

interface Props {
	modelValue?: boolean
}
const props = withDefaults(defineProps<Props>(), { modelValue: false })
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void; (e: 'confirm'): void }>()

const localePath = useLocalePath()
const router = useRouter()
const generalStore = useGeneralStore()
const { t: $t } = useI18n()

// Estado local inmediato — no depende de la prop para cerrarse
const localVisible = ref(props.modelValue)

// Acción ejecutada exactamente una vez
const acted = ref(false)

watch(
	() => props.modelValue,
	(v) => {
		const snapshot = getClientAgeGateSnapshot(ageVerified.value)
		localVisible.value = snapshot?.acted ? false : v
	},
)

const ageVerified = useCookie<boolean | null>('age_verified', {
	maxAge: 60 * 60 * 24 * 30,
	path: '/',
	sameSite: 'lax',
	secure: import.meta.env.PROD,
})

const syncPreHydrationAction = (payload?: { isAdult?: boolean | null }) => {
	localVisible.value = false
	acted.value = true
	generalStore.SET_STATE_GENERAL({ type: 'show_modal_age_verification', value: false })

	if (typeof payload?.isAdult === 'boolean') {
		ageVerified.value = payload.isAdult
		generalStore.SET_STATE_GENERAL({ type: 'is_adult', value: payload.isAdult })
	}
}

if (import.meta.client) {
	const snapshot = getClientAgeGateSnapshot(ageVerified.value)
	if (snapshot?.acted) {
		syncPreHydrationAction({ isAdult: snapshot.isAdult })
	}

	window.addEventListener('age-gate:acted', (event) => {
		const customEvent = event as CustomEvent<{ isAdult?: boolean | null }>
		syncPreHydrationAction(customEvent.detail)
	})
}

const textHtml = computed(() => {
	return $t(
		'Luckywise supports conscious gaming and cares about your well-being. If you need support or information, visit our {page} page.',
		{
			page: `<a href="${localePath('/responsible-gaming')}" class="underline">${$t('Responsible Gaming')}</a>`,
		},
	)
})

// Cierre inmediato: opera sobre estado local primero, luego notifica al padre
const closeModal = () => {
	localVisible.value = false
	emit('update:modelValue', false)
	generalStore.SET_STATE_GENERAL({ type: 'show_modal_age_verification', value: false })
}

const onConfirm = async () => {
	if (acted.value) return
	acted.value = true

	closeModal()

	await nextTick()

	ageVerified.value = true
	generalStore.SET_STATE_GENERAL({ type: 'is_adult', value: true })
	emit('confirm')
}

const onDecline = async () => {
	if (acted.value) return
	acted.value = true

	closeModal()

	await nextTick()

	ageVerified.value = false
	generalStore.SET_STATE_GENERAL({ type: 'is_adult', value: false })

	queueMicrotask(() => {
		void router.push(localePath('/access-denied'))
	})
}
</script>

<template>
	<div
		v-if="localVisible"
		id="ageVerificationModal"
		data-age-modal
		class="fixed inset-0 z-[9999] age-modal"
	>
		<div class="absolute inset-0 bg-[#040404D4] opacity-80 pointer-events-none"></div>

		<div class="absolute inset-0 grid place-items-center p-5 pointer-events-auto">
			<div class="w-full max-w-[600px] rounded-[10px] bg-white border border-[#BBBBBB] shadow-lg pointer-events-auto">
				<div class="flex items-center gap-2.5 px-6 py-5 border-b border-[#D3D3D3]">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						class="h-6 w-6"
						viewBox="0 0 24 24"
						fill="#FFAB00"
					>
						<path
							d="M4.47 21h15.06c1.54 0 2.5-1.67 1.73-3L13.73 4.99c-.77-1.33-2.69-1.33-3.46 0L2.74 18c-.77 1.33.19 3 1.73 3M12 14c-.55 0-1-.45-1-1v-2c0-.55.45-1 1-1s1 .45 1 1v2c0 .55-.45 1-1 1m1 4h-2v-2h2z"
						/>
					</svg>
					<h3 class="text-[22px] font-semibold leading-[1.36] text-black">
						{{ $t('Age verification') }}
					</h3>
				</div>

				<div class="space-y-6 px-6 py-4 leading-6">
					<h4 class="text-base sm:text-[18px] font-bold text-black">
						{{ $t('You must be 18+ to view this website') }}
					</h4>
					<p class="text-sm sm:text-base sm:font-medium text-[#a1a1a1]">
						{{
							$t(
								'Before you start the fun, we need to confirm your age. Access is permitted only for users 18 and over',
							)
						}}.
					</p>
					<p
						class="text-sm sm:text-base sm:font-medium text-[#a1a1a1]"
						v-html="textHtml"
					></p>
				</div>

				<div class="flex flex-col sm:flex-row justify-end items-center gap-4 px-6 py-5">
					<button
						type="button"
						data-age-action="decline"
						class="rounded-full px-5 py-2 border border-primary-600 text-primary-600 font-semibold w-full sm:w-auto hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-60"
						:disabled="acted"
						@click="onDecline"
					>
						{{ $t('No, I am under 18') }}
					</button>
					<button
						type="button"
						data-age-action="confirm"
						class="rounded-full px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold w-full sm:w-auto focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700 disabled:opacity-60"
						:disabled="acted"
						@click="onConfirm"
					>
						{{ $t('Yes, I am 18 or older') }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>
